import { isEmpty } from 'lodash';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import {
  Application,
  ClientApplicationConfig,
  DashboardLayouts,
  defaultSidebar,
} from '@monorepo/types';
import { getEnv } from '@hub/helpers/getters.store';
import { EnvService } from '@hub/services/core/env-service';

export class ApplicationConfigStore {
  @observable
  applicationConfig: ClientApplicationConfig = {
    dashboardLayout: DashboardLayouts.TTPDashboard,
    website: '',
    termsAndConditions: '',
    privacyPolicy: '',
    tradingPracticesUrl: '',
    name: '',
    isBlacklisted: false,
    discordLink: '',
    youtubeLink: '',
    facebookLink: '',
    twitterLink: '',
    instagramLink: '',
    tiktokLink: '',
    linkedinLink: '',
    sidebar: defaultSidebar,
    contracts: [],
    tradingSystems: [],
    banners: [],
    suggestedComments: [],
    enableMigrationSync: false,
    minimumWithdrawAmount: 0,
    userTermsAndConditions: undefined,
    contestTermsAndConditions: undefined,
    affiliateTermsAndConditions: undefined,
    i18nArrays: undefined,
    companyDetails: {
      companyName: '',
      companyTitle: '',
      city: '',
      street: '',
      address: '',
      country: '',
      zipCode: '',
    },
    failedPaymentsConfig: {
      enabled: true,
      failedPaymentThreshold: 10,
      weekStartTime: 'Monday',
      timezone: 'UTC',
      whitelistedEmails: [],
      userHubMessage: '',
      emailAlertRecipient: ['chen@the5ers.com'],
      emailAlertCC: [],
      reactivationEmailSubject: 'Account reactivation',
      reactivationEmailBody: '',
      emailSender: 'chen@the5ers.com',
      versionInfo: {
        version: 0,
        updatedAt: new Date(),
        updatedBy: {
          adminId: '',
          firstName: '',
          lastName: '',
        },
      },
    },
  };

  constructor() {
    makeObservable(this);
  }

  updateFromApiResponse(appConfig: ClientApplicationConfig) {
    runInAction(() => {
      this.applicationConfig = {
        ...this.applicationConfig,
        ...appConfig,
        sidebar: isEmpty(appConfig.sidebar)
          ? defaultSidebar
          : appConfig.sidebar,
      };
    });
  }

  @computed
  get sellableTs() {
    return (
      this.applicationConfig.tradingSystems
        ?.filter((ts) => ts.sellable)
        .map((ts) => ts.tradingSystem) || []
    );
  }

  @computed
  get applicationTitle() {
    const brandName =
      EnvService.application === Application.TTP ? 'TTP' : 'The5ers';
    if (!this.applicationConfig.name) {
      return `${brandName} Trader Hub – Funded Accounts, Dashboard & Tools`;
    }
    return EnvService.application === Application.THE5ERS
      ? `${brandName} – Trader Zone`
      : `${brandName} – HUB`;
  }

  @action
  initCountryFilter = async () => {
    const {
      apiFactory: { applicationConfigService },
    } = getEnv();
    try {
      if (this.applicationConfig?.countryFilter) {
        return;
      }
      const countryFilter = await applicationConfigService.getCountryFilter();

      this.applicationConfig.countryFilter = countryFilter;
    } catch (e) {
      console.error('Error fetching country filter', e);
      throw e;
    }
  };

  @action
  initUserTermsAndConditions = async () => {
    const {
      apiFactory: { applicationConfigService },
    } = getEnv();
    try {
      // Check if already initialized and not empty
      if (
        this.applicationConfig?.userTermsAndConditions &&
        Object.keys(this.applicationConfig.userTermsAndConditions).length > 0
      ) {
        return;
      }

      const userTermsAndConditions =
        await applicationConfigService.getUserTermsAndConditions();

      runInAction(() => {
        this.applicationConfig.userTermsAndConditions =
          userTermsAndConditions || {};
      });
    } catch (e) {
      console.error('Error fetching user terms and conditions', e);
      throw e;
    }
  };
}
